import { z } from 'zod';
import { ShiftLogSummarySchema } from './shift-log';

/**
 * SITREP engine contracts (Phase SITREP.1) — auto-generated Situation Reports.
 *
 * Per Ontario IMS Guidance v2.0 §2.2, a "Situation Report" (SITREP) is a named
 * output of the situation unit. The engine consumes the E.1 ingest layer + F.1
 * fusion (high_risk_hotspots) + R.3 dispatch state (dispatch_recommendations)
 * within a scope and renders a doctrinally-named situation summary.
 *
 * v1 scope (deliberately bounded): fire-focused, on-demand generation, side-panel
 * view, markdown rendering, persisted record, audit logging. Excludes (Phase 2):
 * PDF, email/webhook forwarding (stubbed), scheduled generation, briefing-up
 * view, multi-hazard templates (flood / ice storm get their own templates when
 * those phases land). See Product Dev/build.md.
 */

/* ------------------------------------------------------------------ *
 * Scope
 * ------------------------------------------------------------------ */

/**
 * 'region' (bbox) | 'incident' (single fire/hotspot click-scope) | 'incident_anchored'
 * (SITREP v3.2.b — anchored to an operational period; the bbox is derived from the
 * incident centroid, with scope_payload.derived_from='incident_centroid' for
 * traceability). Legacy anchored SITREPs persisted as 'region' keep rendering —
 * the render layer only consumes the bbox, not scope_type.
 */
export const SitrepScopeTypeSchema = z.enum([
  'region',
  'incident',
  'incident_anchored',
  // W4e #427b — a single community, scoped to its service-area polygon (radius
  // fallback). The request names only the community; the server derives the zone,
  // takes ST_Envelope → the gather bbox, and persists the true polygon + provenance.
  'community',
]);
export type SitrepScopeType = z.infer<typeof SitrepScopeTypeSchema>;

export const SitrepHazardFocusSchema = z.enum(['wildfire', 'multi-hazard', 'flood', 'ice_storm']);
export type SitrepHazardFocus = z.infer<typeof SitrepHazardFocusSchema>;

export const SitrepStatusSchema = z.enum(['draft', 'finalized', 'forwarded', 'archived']);
export type SitrepStatus = z.infer<typeof SitrepStatusSchema>;

/** ICS-209 block 3 (SITREP v3.1) — 'standalone' is the legacy un-anchored
 *  on-demand SITREP; the rest form an incident's Initial→Update→Final chain. */
export const SitrepReportVersionSchema = z.enum(['standalone', 'initial', 'update', 'final']);
export type SitrepReportVersion = z.infer<typeof SitrepReportVersionSchema>;

/**
 * Operator-facing labels for `report_version` (W6 raw-enum legibility, ledger §3.D) —
 * CASING ONLY. The Initial / Update / Final WORDING is owned by the §3.A / W5 wave
 * (it stays a descriptive caption beside the verbatim "situation report"), so this only
 * title-cases the raw enum; `standalone` reads "Standalone Report". Do NOT reword here.
 */
export const SITREP_REPORT_VERSION_LABELS: Record<SitrepReportVersion, string> = {
  standalone: 'Standalone Report',
  initial: 'Initial',
  update: 'Update',
  final: 'Final',
};

/** Title-cased `report_version` label; an unrecognised value falls back to the
 *  standalone caption — never the raw lowercase token. */
export function sitrepReportVersionLabel(version: string): string {
  return (
    SITREP_REPORT_VERSION_LABELS[version as SitrepReportVersion] ??
    SITREP_REPORT_VERSION_LABELS.standalone
  );
}

/** region scope: a bounding box (west < east, south < north). */
export const SitrepRegionScopeSchema = z.object({
  west: z.number().min(-180).max(180),
  south: z.number().min(-90).max(90),
  east: z.number().min(-180).max(180),
  north: z.number().min(-90).max(90),
});
export type SitrepRegionScope = z.infer<typeof SitrepRegionScopeSchema>;

/** incident scope: a single fire or fused hotspot, OR an Area Command parent.
 *  'area_command' (R5 Slice 4, #514) rides the existing 'incident' scope_type — the
 *  DB CHECK (region|incident|incident_anchored) is unchanged, so NO migration — and is
 *  discriminated here so the route takes the SEMANTIC-membership Area Command aggregate
 *  path (members = live parent_incident_id children) instead of a geographic gather. */
export const SitrepIncidentScopeSchema = z.object({
  incident_id: z.string().uuid(),
  incident_type: z.enum(['high_risk_hotspot', 'active_fire', 'area_command']),
});
export type SitrepIncidentScope = z.infer<typeof SitrepIncidentScopeSchema>;

/** community scope (W4e #427b) — the REQUEST names ONLY the community; the server
 *  derives the zone (service_area → StatCan populated_place → radius). */
export const SitrepCommunityScopeSchema = z.object({
  community_id: z.string().uuid(),
});
export type SitrepCommunityScope = z.infer<typeof SitrepCommunityScopeSchema>;

/** The PERSISTED community scope_payload — the request's community_id PLUS the
 *  server-derived provenance the render layer + a future polygon-true gather read:
 *  the zone's `scope_source`, the ST_Envelope bbox actually gathered (so a bbox-only
 *  render layer keeps working, like the 'incident_anchored' payload), and the true
 *  GeoJSON polygon (persisted this wave for the render layer + #427b-2 tightening,
 *  even though the gather itself is bbox-of-polygon). */
export const SitrepCommunityStoredScopeSchema = SitrepCommunityScopeSchema.extend({
  scope_source: z.enum(['service_area', 'populated_place', 'radius']),
  west: z.number(),
  south: z.number(),
  east: z.number(),
  north: z.number(),
  /** GeoJSON Polygon (single outer ring). `unknown` — the render layer + the
   *  deferred polygon-true gather parse it; the SITREP route never re-reads it. */
  polygon: z.unknown().optional(),
});
export type SitrepCommunityStoredScope = z.infer<typeof SitrepCommunityStoredScopeSchema>;

/* ------------------------------------------------------------------ *
 * Request — POST /api/sitreps
 * ------------------------------------------------------------------ */

/**
 * The client names the scope; the server snapshots all state within it. A region
 * scope carries a bbox; an incident scope carries the incident id + type. The
 * refinement keeps the right payload shape per scope_type.
 */
export const SitrepRequestSchema = z
  .object({
    /** Optional when anchoring to an operational period — the spatial scope is
     *  then derived from the incident centroid (SITREP v3.1). Required otherwise. */
    scope_type: SitrepScopeTypeSchema.optional(),
    scope_payload: z
      .union([SitrepRegionScopeSchema, SitrepIncidentScopeSchema, SitrepCommunityScopeSchema])
      .optional(),
    /** SITREP v3.2.a — optional. When omitted on an anchored request, the route
     *  auto-maps it from the linked incident's hazard_type; when omitted on a
     *  non-anchored request it defaults to 'wildfire' (legacy behaviour). An
     *  explicit value always wins (operator override). */
    hazard_focus: SitrepHazardFocusSchema.optional(),
    /** SITREP v3.1 — anchor this SITREP to an operational period. When set, the
     *  report_version (initial/update/final) + sequence derive automatically from
     *  the incident's existing chain, and incident_id is inferred from the period
     *  if not supplied. */
    operational_period_id: z.string().uuid().optional(),
    incident_id: z.string().uuid().optional(),
    /** SITREP v3.2 — explicit report-version override for an anchored SITREP, used
     *  by tsr-sitrep-scheduler-worker to force the period-closing 'final' (the
     *  natural derivation only yields 'final' once the *incident* is closed, but a
     *  period close should produce that period's final report). Ignored on the
     *  non-anchored path. When omitted the natural initial→update→final derivation
     *  applies. */
    report_version: z.enum(['initial', 'update', 'final']).optional(),
    /** SITREP v3.2 — set true by the scheduler so the action is audited as
     *  'auto_generate_sitrep' (machine-issued) rather than 'generate_sitrep'. */
    auto_generated: z.boolean().optional(),
    /** #148 Handover SITREP — when set (ISO-8601, the shift-start anchor), the
     *  rendered SITREP embeds a "Shift Handover Log" section summarising the
     *  operator actions since this time, on top of whatever hazard template is
     *  selected (NO template fork — an additive shared section). */
    shift_log_since: z.string().datetime({ offset: true }).optional(),
    session_id: z.string().min(1).max(128),
  })
  .superRefine((val, ctx) => {
    // Either an operational-period anchor OR an explicit (scope_type + payload).
    if (!val.operational_period_id && !val.scope_type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'scope_type is required unless operational_period_id is provided',
        path: ['scope_type'],
      });
      return;
    }
    // Anchored requests may omit scope_payload (derived from the incident).
    if (!val.scope_type) return;
    if (!val.scope_payload) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'scope_payload is required when scope_type is provided',
        path: ['scope_payload'],
      });
      return;
    }
    if (val.scope_type === 'region') {
      const r = SitrepRegionScopeSchema.safeParse(val.scope_payload);
      if (!r.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'region scope_payload must be a bbox {west,south,east,north}',
          path: ['scope_payload'],
        });
        return;
      }
      if (r.data.west >= r.data.east || r.data.south >= r.data.north) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'bbox must be west<east and south<north',
          path: ['scope_payload'],
        });
      }
    } else if (val.scope_type === 'community') {
      if (!SitrepCommunityScopeSchema.safeParse(val.scope_payload).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'community scope_payload must be {community_id}',
          path: ['scope_payload'],
        });
      }
    } else if (!SitrepIncidentScopeSchema.safeParse(val.scope_payload).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'incident scope_payload must be {incident_id, incident_type}',
        path: ['scope_payload'],
      });
    }
  });
export type SitrepRequest = z.infer<typeof SitrepRequestSchema>;

/* ------------------------------------------------------------------ *
 * Structured data (the machine-readable mirror of the narrative)
 * ------------------------------------------------------------------ */

export const SitrepFireSchema = z.object({
  id: z.string(),
  name: z.string(),
  stage_of_control_status: z.string().nullable(),
  hectares_estimated: z.number().nullable(),
  agency: z.string().nullable(),
  observed_at: z.string(),
});

export const SitrepHotspotSchema = z.object({
  id: z.string(),
  confidence: z.number(),
  response_level: z.number(),
  corroborating_signals: z.array(z.string()),
});

export const SitrepDispatchSchema = z.object({
  id: z.string(),
  recommendation_type: z.string(),
  recommended_assets: z.array(z.string()),
  status: z.string(),
  created_at: z.string(),
});

export const SitrepCapAlertSchema = z.object({
  headline: z.string().nullable(),
  event: z.string().nullable(),
  severity: z.string().nullable(),
  urgency: z.string().nullable(),
  certainty: z.string().nullable(),
  area_desc: z.string().nullable(),
});

export const SitrepSpikeSchema = z.object({
  label: z.string(),
  value: z.number(),
  observed_at: z.string(),
});

export const SitrepPlumeSchema = z.object({
  density: z.string().nullable(),
  analysis_date: z.string(),
});

/**
 * W.1 — community wellness/headcount rollup (ICS-209 blocks 31 + 34). Present
 * only on a SITREP anchored to an incident that has linked communities; null
 * otherwise. Aggregates the latest headcount + latest wellness check per community
 * tied to the incident. `communities` lists the per-community latest figures so
 * the narrative can name gaps; the top-level fields are the incident-wide rollup.
 */
export const SitrepCommunityImpactSchema = z.object({
  community_count: z.number().int(),
  total_population: z.number().int(),
  total_vulnerable_population: z.number().int(),
  // Aggregate headcount (latest snapshot per community).
  total_evacuated: z.number().int(),
  total_remaining: z.number().int(),
  total_accounted_for: z.number().int(),
  total_unreachable: z.number().int(),
  // Wellness check status rollup (latest check per community).
  wellness_complete: z.number().int(),
  wellness_in_progress: z.number().int(),
  wellness_planned: z.number().int(),
  wellness_gap_identified: z.number().int(),
  total_warming_centres_active: z.number().int(),
  communities: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      community_type: z.string(),
      population: z.number().int().nullable(),
      vulnerable_population: z.number().int().nullable(),
      evacuated: z.number().int().nullable(),
      remaining: z.number().int().nullable(),
      accounted_for: z.number().int().nullable(),
      unreachable: z.number().int().nullable(),
      wellness_status: z.string().nullable(),
      warming_centres_active: z.number().int().nullable(),
      vulnerable_residents_unreachable: z.number().int().nullable(),
    }),
  ),
});
export type SitrepCommunityImpact = z.infer<typeof SitrepCommunityImpactSchema>;

/**
 * R.4.1 → R.4.2 — resource-request rollup for an anchored SITREP (ICS-209 block
 * 39 Critical Resource Needs + block 49 Resources committed). Present only on a
 * SITREP anchored to an incident that has linked resource requests; null
 * otherwise. Counts open requests across the full 7-state lifecycle and lists the
 * high-urgency open ones.
 */
export const SitrepResourceRequestsSchema = z.object({
  total_open: z.number().int(),
  pending: z.number().int(),
  assigned: z.number().int(),
  /** R.4.2 lifecycle-distribution counts. */
  checked_in: z.number().int(),
  staging: z.number().int(),
  committed: z.number().int(),
  /** R.4.2 — demobilized in this incident (terminal; counted but not "open"). */
  demobilized: z.number().int(),
  /** High-urgency (urgent/immediate) open requests, for the narrative + block 39. */
  high_urgency: z.array(
    z.object({
      request_number: z.string(),
      request_status: z.string(),
      urgency: z.string(),
      target: z.string(),
    }),
  ),
});
export type SitrepResourceRequests = z.infer<typeof SitrepResourceRequestsSchema>;

/**
 * R.4.2 — staging-areas rollup for an anchored SITREP (ICS-209 block 49). The
 * open staging areas on the incident + how many resources are staged at each.
 * Null when the incident has no open staging areas.
 */
export const SitrepStagingAreasSchema = z.object({
  total_open: z.number().int(),
  areas: z.array(
    z.object({
      name: z.string(),
      staged_count: z.number().int(),
    }),
  ),
});
export type SitrepStagingAreas = z.infer<typeof SitrepStagingAreasSchema>;

/**
 * Task #64 — sector rollup for an anchored SITREP (ICS-209 block 30 Significant
 * Events, organized by sector; IMS §5.3 divisions). The OPEN sectors on the
 * incident, each with its lead label and counts of anomalies + response
 * resources geographically inside its polygon. Null when the incident has no
 * open sectors.
 */
export const SitrepSectorsSchema = z.object({
  total_open: z.number().int(),
  sectors: z.array(
    z.object({
      name: z.string(),
      lead_label: z.string().nullable(),
      anomaly_count: z.number().int(),
      resource_count: z.number().int(),
    }),
  ),
});
export type SitrepSectors = z.infer<typeof SitrepSectorsSchema>;

/**
 * Multi-hazard (ICE STORM) — electric-utility power-outage rollup in scope (§4.28
 * utility_outages). Present only on an ice_storm / multi-hazard SITREP; null
 * otherwise. Counts the ACTIVE outages (active + crew_dispatched) intersecting the
 * report scope, the customers affected, the crew-dispatched share, and a
 * by-utility breakdown — the lead picture of the ice-storm template.
 */
export const SitrepUtilityOutagesSchema = z.object({
  total_outages: z.number().int(),
  total_customers_affected: z.number().int(),
  crew_dispatched_outages: z.number().int(),
  by_utility: z.array(
    z.object({
      utility: z.string(),
      outages: z.number().int(),
      customers_affected: z.number().int(),
      crew_dispatched: z.number().int(),
    }),
  ),
});
export type SitrepUtilityOutages = z.infer<typeof SitrepUtilityOutagesSchema>;

/**
 * Multi-hazard (FLOOD) — live response aircraft in scope (the airlift). Present
 * only on a flood / multi-hazard SITREP; null otherwise. The tracked aircraft
 * (R.1d/R.1e) whose last position falls inside the report scope, summarised by
 * role with a named sample for the narrative.
 */
export const SitrepAircraftSchema = z.object({
  total: z.number().int(),
  by_role: z.array(z.object({ role: z.string(), count: z.number().int() })),
  aircraft: z.array(
    z.object({
      tail_number: z.string(),
      aircraft_type: z.string().nullable(),
      aircraft_role: z.string().nullable(),
      operator_org: z.string().nullable(),
    }),
  ),
});
export type SitrepAircraft = z.infer<typeof SitrepAircraftSchema>;

/* ------------------------------------------------------------------ *
 * In-region non-fire signals (#427a — non-fire REGION SITREP rollup)
 *
 * Gathered ONLY for a region-scope ("current map view") SITREP, never for an
 * anchored/incident-click report. These are the in-region signals that pre-#427a
 * the region report never gathered — the Kashechewan-flood false-negative where a
 * region SITREP printed "Active Wildland Fires 0 / Urgent Anomalies 0 / Public
 * Alerts (CAP) 0" while an RL4 flood anomaly + an assist + an evac order were all
 * in scope. Additive: present only when the signal exists, so a fire-only region
 * report (and the wildfire golden) renders none of these sections.
 * ------------------------------------------------------------------ */

/** Flood gauge anomaly in scope (WSC hydrometric rate-of-rise; flood_anomalies).
 *  Its response_level (1..4) folds into the SITREP header responseLevelMax. */
export const SitrepFloodAnomalySchema = z.object({
  id: z.string(),
  station_name: z.string().nullable(),
  river: z.string().nullable(),
  /** 'watch' (over rise threshold) | 'warning' (over rapid threshold). */
  anomaly_type: z.string(),
  response_level: z.number().int(),
  rate_of_rise_cm_per_hr: z.number().nullable(),
  water_level_m: z.number().nullable(),
  status: z.string(),
});
export type SitrepFloodAnomaly = z.infer<typeof SitrepFloodAnomalySchema>;

/** Active road closure / restriction in scope (511on/511ab/drivebc; road_closures). */
export const SitrepRoadClosureSchema = z.object({
  id: z.string(),
  roadway_name: z.string().nullable(),
  province: z.string(),
  /** TRUE = full closure, FALSE = lane/partial restriction. */
  is_closure: z.boolean(),
  severity: z.string().nullable(),
  direction: z.string().nullable(),
  cause: z.string().nullable(),
});
export type SitrepRoadClosure = z.infer<typeof SitrepRoadClosureSchema>;

/** Active evacuation order/alert in scope — EXTERNAL/ingested (arcgis_public_features
 *  feed_key='geobc-evac-orders'). Rendered attributed to its source feed, NEVER as a
 *  bare COP-issued order (operator-declared evac is #445-(3), unshipped). */
export const SitrepEvacOrderSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  /** Per-feed escalation level — 'order' | 'alert'. */
  severity: z.string().nullable(),
  status: z.string(),
  event_type: z.string().nullable(),
  population: z.number().int().nullable(),
  homes: z.number().int().nullable(),
  /** Source-feed discriminator (drives the rendered "GeoBC …" source label). */
  feed_key: z.string(),
  /** OGL/source-agency attribution string, verbatim from the ingest row. */
  attribution: z.string(),
});
export type SitrepEvacOrder = z.infer<typeof SitrepEvacOrderSchema>;

/** Open, NOT-YET-OWNED assistance request in scope (assistance_requests WHERE
 *  incident_id IS NULL). A bridged assist (incident_id set) already rolls up in the
 *  ANCHORED report's resources, so the region report excludes it to avoid
 *  double-counting the need across reports. Its response_level (triage RL) folds
 *  into the header responseLevelMax. */
export const SitrepRegionAssistSchema = z.object({
  id: z.string(),
  requestor_name: z.string(),
  requestor_type: z.string(),
  urgency: z.string(),
  status: z.string(),
  response_level: z.number().int().nullable(),
  resource_categories: z.array(z.string()),
});
export type SitrepRegionAssist = z.infer<typeof SitrepRegionAssistSchema>;

/** Active OPERATOR-DECLARED evacuation order in scope (#445-(3),
 *  operator_evacuation_orders WHERE status='active'). The COP-RECORDED order,
 *  attributed to the named issuing authority — NEVER conflated with the EXTERNAL
 *  GeoBC `evac_orders` (SitrepEvacOrderSchema above). Its level maps to a response
 *  level (order→4, alert→2) that folds into the header responseLevelMax. */
export const SitrepOperatorEvacOrderSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  /** 'alert' | 'order'. */
  level: z.string(),
  status: z.string(),
  /** The KIND of issuing body (municipal | first_nation | provincial | …). */
  issuing_authority_type: z.string(),
  /** The NAMED issuing body the COP recorded the order on behalf of. */
  issuing_authority_name: z.string(),
  /** #458 — the coordinating authority (Far-North tripartite case), nullable. Rendered
   *  "coordinated by <name>" as a SECONDARY clause AFTER "issued by …", never above it. */
  coordinating_authority_type: z.string().nullable(),
  coordinating_authority_name: z.string().nullable(),
  population_estimate: z.number().int().nullable(),
  /** Derived from level — order→4, alert→2 (nullable for safety). */
  response_level: z.number().int().nullable(),
});
export type SitrepOperatorEvacOrder = z.infer<typeof SitrepOperatorEvacOrderSchema>;

/**
 * R5 Area Command Slice 4 (#514) — the roster of incidents grouped under an Area
 * Command parent, for an Area-Command-scoped SITREP. Present only on that scope;
 * null/absent otherwise. `response_level` is DERIVED from each child's linked source
 * hazard (high_risk_hotspot / flood / ice-storm / cap / OC-fire response_level) — a
 * manually-declared child (no source) is null and drops out of the header max-fold.
 * The children are Ontario IMS App A #35 INCIDENTS "under area command" (App A #85) —
 * never "members" (the term appears nowhere in §4/App A; see the render-side lock).
 */
export const SitrepAreaCommandMemberSchema = z.object({
  incident_id: z.string(),
  incident_number: z.string(),
  name: z.string(),
  lifecycle_state: z.string(),
  response_level: z.number().int().nullable(),
});
export type SitrepAreaCommandMember = z.infer<typeof SitrepAreaCommandMemberSchema>;

/**
 * #620 — the anchored incident's IC-assessed §5 Response Level, SNAPSHOTTED into the
 * SITREP's structured_data at generation time (never read live). A SITREP is a FROZEN
 * point-in-time record that gets forwarded + PDF'd; a live join would let a days-old
 * report silently re-render with TODAY's IC assessment — a falsification of the record.
 *
 * Present ONLY on an anchored SITREP whose incident carries a pin (`operator_set_response_level`
 * is non-null on the incidents row, migration 0148). Null/absent on region / community /
 * incident-click / Area-Command / anchored-without-pin reports. Drives the dual-display header:
 * the §5 RESPONSE Level as the primary badge + the machine THREAT sub-line + the divergence cue.
 */
export const SitrepIncidentResponseLevelSchema = z.object({
  /** The IC's assessed §5 Response Level (1..4) — the pin, snapshotted. */
  assessed_level: z.number().int().min(1).max(4),
  /** The assessing Incident Commander (operator_rl_by snapshot); null when the pin
   *  predates the attribution column or was set without a command-officer name. */
  assessed_by: z.string().nullable(),
  /** When the latest Set landed (operator_rl_at, ISO); null when unavailable. */
  assessed_at: z.string().nullable(),
  /** The IC's assessment reason (operator_rl_reason); null when unavailable. */
  reason: z.string().nullable(),
});
export type SitrepIncidentResponseLevel = z.infer<typeof SitrepIncidentResponseLevelSchema>;

export const SitrepAreaCommandSchema = z.object({
  name: z.string(),
  members: z.array(SitrepAreaCommandMemberSchema),
  /** R5 Slice 5 (#541, migration 0129) — the Area Command's OWN broad objectives (App A #2:
   *  "develop broad objectives for their assigned area"), ordered. `.default([])` keeps an
   *  older persisted structured_data (pre-Slice-5, no field) parsing to an empty list. */
  objectives: z.array(z.string()).default([]),
  /** R5 Slice 5 (#541) — the Area Command's OWN priorities for the sharing of critical
   *  resources across sites (App A #2), ordered. A priority STATEMENT, never a child
   *  allocation/tasking (§4.6). `.default([])` for backward-compatible parsing. */
  resource_priorities: z.array(z.string()).default([]),
});
export type SitrepAreaCommand = z.infer<typeof SitrepAreaCommandSchema>;

/** The full machine-readable payload persisted alongside the narrative. */
export const SitrepStructuredDataSchema = z.object({
  active_fires: z.array(SitrepFireSchema),
  high_risk_hotspots: z.array(SitrepHotspotSchema),
  dispatch_recommendations: z.array(SitrepDispatchSchema),
  cap_alerts: z.array(SitrepCapAlertSchema),
  aqhi_spikes: z.array(SitrepSpikeSchema),
  purpleair_spikes: z.array(SitrepSpikeSchema),
  hms_plumes: z.array(SitrepPlumeSchema),
  population_centres: z.array(z.string()),
  /** W.1 — community wellness/headcount rollup; present only on anchored SITREPs
   *  whose incident has linked communities (ICS-209 blocks 31 + 34). */
  community_impact: SitrepCommunityImpactSchema.nullable().optional(),
  /** R.4.1 — resource-request rollup; present only on anchored SITREPs whose
   *  incident has linked resource requests (ICS-209 block 39). */
  resource_requests: SitrepResourceRequestsSchema.nullable().optional(),
  /** R.4.2 — staging-areas rollup; present only on anchored SITREPs whose
   *  incident has open staging areas (ICS-209 block 49). */
  staging_areas: SitrepStagingAreasSchema.nullable().optional(),
  /** Task #64 — sector rollup; present only on anchored SITREPs whose incident
   *  has open sectors (ICS-209 block 30 Significant Events by sector). */
  sectors: SitrepSectorsSchema.nullable().optional(),
  /** Ice storm — utility-outage rollup in scope (§4.28); present only on an
   *  ice_storm / multi-hazard SITREP. */
  utility_outages: SitrepUtilityOutagesSchema.nullable().optional(),
  /** Flood — live response aircraft in scope (the airlift); present only on a
   *  flood / multi-hazard SITREP. */
  aircraft: SitrepAircraftSchema.nullable().optional(),
  /** #427a — in-region flood gauge anomalies; present only on a region-scope
   *  SITREP (gathered bbox-scoped, never for anchored/incident reports). */
  flood_anomalies: z.array(SitrepFloodAnomalySchema).nullable().optional(),
  /** #427a — in-region active road closures; region-scope only. */
  road_closures: z.array(SitrepRoadClosureSchema).nullable().optional(),
  /** #427a — in-region active evacuation orders/alerts (EXTERNAL/ingested);
   *  region-scope only. */
  evac_orders: z.array(SitrepEvacOrderSchema).nullable().optional(),
  /** #427a — in-region open, not-yet-owned assistance requests (incident_id IS
   *  NULL); region-scope only. */
  region_assists: z.array(SitrepRegionAssistSchema).nullable().optional(),
  /** #445-(3) — in-region active OPERATOR-DECLARED evacuation orders (recorded
   *  in-COP, attributed to the issuing authority); region-scope only. Parallel to
   *  `evac_orders` (the EXTERNAL GeoBC feed) — never conflated. */
  operator_evac_orders: z.array(SitrepOperatorEvacOrderSchema).nullable().optional(),
  /** #148 Handover SITREP — the shift-log summary embedded when the request
   *  carries `shift_log_since`; null otherwise. The narrative's "Shift Handover
   *  Log" section is rendered from this. */
  shift_log: ShiftLogSummarySchema.nullable().optional(),
  /** R5 Slice 4 (#514) — the Area Command member roster; present ONLY on an
   *  Area-Command-scoped SITREP (its presence is how the renderer + PDF select the
   *  Area Command narrative). Null/absent on every hazard/region/incident report. */
  area_command: SitrepAreaCommandSchema.nullable().optional(),
  /** #620 — the anchored incident's IC-assessed §5 Response Level SNAPSHOT. Present ONLY
   *  on an anchored SITREP whose incident carries a pin; null/absent otherwise. Drives the
   *  dual-display header (Response Level primary + Threat sub-line + divergence). `.nullable()
   *  .optional()` ⇒ every pre-#620 persisted row parses unchanged. */
  incident_response_level: SitrepIncidentResponseLevelSchema.nullable().optional(),
  /** #620 — the MACHINE-only Threat max: the 5-term per-hazard max EXCLUDING the operator
   *  pin (the honest `derived` for the dual-display annotation + the Threat>Response divergence
   *  comparison). NEVER rendered as the header Threat Level — that stays `response_level_max`
   *  (the escalation-safe roll-up). `.nullable().optional()` ⇒ old rows parse unchanged. */
  threat_level_derived: z.number().int().min(1).max(4).nullable().optional(),
});
export type SitrepStructuredData = z.infer<typeof SitrepStructuredDataSchema>;

/* ------------------------------------------------------------------ *
 * Report — POST / GET response
 * ------------------------------------------------------------------ */

/** The created (or retrieved) SITREP, as served to the client. */
export const SitrepReportSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  scope_type: SitrepScopeTypeSchema,
  // Community FIRST: the community stored payload carries west/south/east/north too,
  // so a region-first union would match + STRIP community_id/scope_source/polygon.
  // A region / incident payload lacks community_id, so it falls through correctly.
  scope_payload: z.union([
    SitrepCommunityStoredScopeSchema,
    SitrepRegionScopeSchema,
    SitrepIncidentScopeSchema,
  ]),
  hazard_focus: SitrepHazardFocusSchema,
  title: z.string(),
  summary_markdown: z.string(),
  structured_data: SitrepStructuredDataSchema,
  active_fires_count: z.number().int(),
  /** #446 — the URGENT (Response Level ≥ 3) hotspot count: matches the SITREP
   *  narrative's "Urgent Anomalies" header + the COP's RL≥3 `urgentAnomalies` map
   *  layer. NOT the cardinality of `structured_data.high_risk_hotspots`, which keeps
   *  the FULL set (incl. Slice-2-demoted RL≤2 fusion candidates). */
  high_risk_hotspots_count: z.number().int(),
  open_dispatches_count: z.number().int(),
  response_level_max: z.number().int().min(1).max(4).nullable(),
  status: SitrepStatusSchema,
  /** SITREP v3.1 anchoring (null/standalone for legacy on-demand SITREPs). */
  report_version: SitrepReportVersionSchema,
  report_sequence: z.number().int().nullable(),
  incident_id: z.string().nullable(),
  operational_period_id: z.string().nullable(),
});
export type SitrepReport = z.infer<typeof SitrepReportSchema>;

/** Compact list-item (the recent-SITREPs panel doesn't need the full markdown). */
export const SitrepListItemSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  scope_type: SitrepScopeTypeSchema,
  hazard_focus: SitrepHazardFocusSchema,
  title: z.string(),
  active_fires_count: z.number().int(),
  /** #446 — the URGENT (Response Level ≥ 3) hotspot count: matches the SITREP
   *  narrative's "Urgent Anomalies" header + the COP's RL≥3 `urgentAnomalies` map
   *  layer. NOT the cardinality of `structured_data.high_risk_hotspots`, which keeps
   *  the FULL set (incl. Slice-2-demoted RL≤2 fusion candidates). */
  high_risk_hotspots_count: z.number().int(),
  open_dispatches_count: z.number().int(),
  response_level_max: z.number().int().min(1).max(4).nullable(),
  status: SitrepStatusSchema,
  /** SITREP v3.1 anchoring. */
  report_version: SitrepReportVersionSchema,
  report_sequence: z.number().int().nullable(),
  incident_id: z.string().nullable(),
  operational_period_id: z.string().nullable(),
});
export type SitrepListItem = z.infer<typeof SitrepListItemSchema>;

export const SitrepListResponseSchema = z.object({
  sitreps: z.array(SitrepListItemSchema),
});
export type SitrepListResponse = z.infer<typeof SitrepListResponseSchema>;
